import { Box, IconButton, styled, TextField } from "@material-ui/core";
import { Mic, Send, Stop } from "@material-ui/icons";
import { type KeyboardEvent, useEffect, useState } from "react";
import {
  EPOCUS_BORDER,
  EPOCUS_ORANGE,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import {
  VOICE_ENGINE_LABELS,
  type VoiceControl,
  type VoiceEngine,
} from "../voice-input";

const Root = styled(Box)({
  flex: "none",
  borderTop: `1px solid ${EPOCUS_BORDER}`,
  backgroundColor: EPOCUS_WHITE,
  padding: "10px 18px",
  paddingBottom: "max(10px, env(safe-area-inset-bottom))",
});

const Row = styled(Box)({ display: "flex", alignItems: "flex-end", gap: 8 });

const SendButton = styled(IconButton)({ width: 44, height: 44 });

const MicButton = styled(IconButton)({ width: 44, height: 44 });

const Hint = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  minHeight: 44,
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
});

const StopButton = styled("button")({
  minHeight: 44,
  padding: "0 12px",
  border: "none",
  background: "none",
  font: "inherit",
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
  cursor: "pointer",
});

const RecordingDot = styled("span")({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: EPOCUS_ORANGE,
  flex: "none",
});

const RecordingHint = styled(Hint)({ justifyContent: "flex-start" });

const Elapsed = styled("span")({ flex: 1 });

const VoiceMessage = styled(Box)({
  paddingTop: 6,
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
});

const EngineRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 6,
  paddingTop: 6,
  fontSize: rem(11),
  color: EPOCUS_TEXT_SECONDARY,
});

const EngineChoice = styled("button")({
  padding: "0 14px",
  minHeight: 44,
  minWidth: 44,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 999,
  background: "none",
  font: "inherit",
  fontSize: rem(11),
  color: EPOCUS_TEXT_SECONDARY,
  cursor: "pointer",
  '&[aria-pressed="true"]': {
    borderColor: EPOCUS_PRIMARY,
    color: EPOCUS_PRIMARY,
    fontWeight: 700,
  },
});

const elapsed = (ms: number) => {
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

export const Composer: FC<{
  textInput?: { placeholder?: string };
  busy: boolean;
  awaitingApproval: boolean;
  awaitingChoice: boolean;
  awaitingScan: boolean;
  voice?: VoiceControl;
  onSend: (text: string) => void;
  onStop: () => void;
}> = ({
  textInput,
  busy,
  awaitingApproval,
  awaitingChoice,
  awaitingScan,
  voice,
  onSend,
  onStop,
}) => {
  const [draft, setDraft] = useState("");

  const said = voice?.transcript;
  const clearTranscript = voice?.clearTranscript;
  useEffect(() => {
    if (!said || !clearTranscript) return;
    // send() sets idle and the transcript together, so a take that finishes on the same render the
    // chat goes busy slips past the cancel below. Its question is already answered and off screen;
    // appending would leave stale speech in the draft for whatever is asked next.
    if (busy) {
      clearTranscript();
      return;
    }
    setDraft((current) => (current ? `${current} ${said}` : said));
    clearTranscript();
  }, [said, clearTranscript, busy]);

  const transcribing = voice?.state === "transcribing";

  // A choice, approval or watch response tapped above this composer starts a turn, and the busy
  // branch below then replaces the composer. An abandoned take left running lands in whatever turn
  // is current when it finishes — the microphone still open with no Stop or Cancel for a recording,
  // or an upload whose transcript appends to the next question's draft.
  const voiceInFlight =
    voice?.state === "recording" || voice?.state === "starting" || transcribing;
  const cancelVoice = voice?.cancel;
  useEffect(() => {
    if (busy && voiceInFlight) cancelVoice?.();
  }, [busy, voiceInFlight, cancelVoice]);

  const submit = () => {
    const text = draft.trim();
    if (!text || busy || transcribing) return;
    setDraft("");
    onSend(text);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    submit();
  };

  if (busy) {
    return (
      <Root>
        <Hint role="status" aria-live="polite">
          <span>Working on it…</span>
          <StopButton type="button" onClick={onStop}>
            Stop
          </StopButton>
        </Hint>
      </Root>
    );
  }

  if (!textInput) {
    const hint = awaitingApproval
      ? "Answer above to carry on."
      : awaitingChoice
        ? "Tap an option above."
        : awaitingScan
          ? "Waiting for a scan to arrive."
          : undefined;

    if (hint) {
      return (
        <Root>
          <Hint role="status" aria-live="polite">
            <span>{hint}</span>
          </Hint>
        </Root>
      );
    }
  }

  if (
    voice?.available &&
    (voice.state === "recording" || voice.state === "starting")
  ) {
    const listening = voice.state === "recording";
    return (
      <Root>
        <RecordingHint>
          {listening && <RecordingDot />}
          <Elapsed>
            {/* The timer changes every tick. Inside the live region a screen reader would
                re-announce the whole row every second for a minute, burying the status it is
                there to convey, so only the status itself is announced. */}
            <span role="status" aria-live="polite">
              {listening ? "Listening…" : "Allow the microphone to start."}
            </span>
            {listening && <span aria-hidden> {elapsed(voice.elapsedMs)}</span>}
          </Elapsed>
          <StopButton type="button" onClick={voice.cancel}>
            Cancel
          </StopButton>
          {listening && (
            <SendButton
              color="primary"
              aria-label="Stop recording"
              onClick={voice.stop}
            >
              <Stop />
            </SendButton>
          )}
        </RecordingHint>
      </Root>
    );
  }

  return (
    <Root>
      <Row>
        <TextField
          fullWidth
          multiline
          maxRows={5}
          variant="outlined"
          size="small"
          value={draft}
          placeholder={textInput?.placeholder ?? "Type a message…"}
          inputProps={{ "aria-label": "Message Dragonfly" }}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
        />
        {voice?.available && (
          <MicButton
            aria-label="Record a message"
            disabled={transcribing}
            onClick={voice.start}
          >
            <Mic />
          </MicButton>
        )}
        <SendButton
          color="primary"
          aria-label="Send"
          disabled={!draft.trim() || transcribing}
          onClick={submit}
        >
          <Send />
        </SendButton>
      </Row>
      {transcribing && (
        <VoiceMessage role="status" aria-live="polite">
          Writing that down…
        </VoiceMessage>
      )}
      {voice?.state === "denied" && (
        <VoiceMessage role="status" aria-live="polite">
          Microphone blocked. Allow it in your browser settings and tap the mic
          again, or type your answer.
        </VoiceMessage>
      )}
      {voice?.state === "error" && voice.message && (
        <VoiceMessage role="status" aria-live="polite">
          {voice.message}
        </VoiceMessage>
      )}
      {voice?.available && voice.engines.length > 1 && (
        <EngineRow>
          <span id="voice-engine-label">Voice</span>
          <Box
            role="group"
            aria-labelledby="voice-engine-label"
            display="flex"
            gridGap={6}
          >
            {voice.engines.map((option: VoiceEngine) => (
              <EngineChoice
                key={option}
                type="button"
                aria-pressed={option === voice.engine}
                disabled={transcribing}
                onClick={() => voice.setEngine(option)}
              >
                {VOICE_ENGINE_LABELS[option]}
              </EngineChoice>
            ))}
          </Box>
        </EngineRow>
      )}
    </Root>
  );
};
